import { Command } from "cmdk";
import * as Popover from "@radix-ui/react-popover";
import { baseQueryAPI } from "../../services/baseQuery";
import { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import { useDebounce } from "@uidotdev/usehooks";

export const Combobox = ({
  filter,
  label,
  onRemove,
  leadType,
  onFilterSave,
}: {
  leadType: number;
  filter: string;
  label: string;
  onRemove: (val: string) => void;
  onFilterSave: (name: string, val: any) => void;
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [filters, setFilters] = useState<any>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 500);
  const debouncedPage = useDebounce(page, 1000);
  const listRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = baseQueryAPI.useListFiltersQuery({
    filter,
    leadType,
    search: debouncedSearch,
    page: debouncedPage,
  });

  useEffect(() => {
    if (data?.data) {
      setFilters(data.data);
    }
  }, [data]);

  const onListScroll = (e: any) => {
    // console.log(e);
    const { scrollTop, scrollHeight, offsetHeight } = e.target;
    const bottom = scrollTop + offsetHeight >= scrollHeight - 50;
    if (bottom && filters.length >= data?.per_page) {
      setPage(page + 1);
    }
  };

  const changeSelected = (val: string) => {
    // if (selected.includes(val)) {
    //   setSelected((current) => current.filter((itm) => itm !== val));
    //   return;
    // }
    setSelected((current) => {
      if (current.includes(val)) {
        return current.filter((itm) => itm !== val);
      }
      return [...current, val];
    });
  };

  const onClear = () => {
    setSelected([]);
    onFilterSave(filter, []);
  };

  return (
    <>
      <Popover.Root>
        <Popover.Anchor>
          <FilterItem
            name={label}
            remove={() => {
              onClear();
              onRemove(filter);
            }}
          />
        </Popover.Anchor>

        <Popover.Content
          align="start"
          className=" bg-white border shadow-lg mt-2"
        >
          <Command>
            <div className="p-2">
              <Command.Input
                placeholder="Search Name"
                className="border outline-none py-2 px-4 font-medium text-gray-500"
                onValueChange={setSearch}
              />
            </div>
            <Command.List
              ref={listRef}
              className="max-h-[300px] overflow-y-auto w-64"
              onScroll={onListScroll}
            >
              {isLoading ? (
                <Command.Loading>Loading...</Command.Loading>
              ) : (
                <>
                  <Command.Empty>No results</Command.Empty>
                  {filters?.map((itm: string) => (
                    <Command.Item
                      key={itm}
                      value={itm}
                      className="px-5 py-3 text-gray-700 font-medium hover:bg-gray-100 hover:font-semibold transition"
                      onSelect={() => changeSelected(itm)}
                    >
                      <div className="flex items-center gap-x-2">
                        <input
                          className="border w-4 h-4"
                          checked={selected.includes(itm)}
                          type="checkbox"
                        />
                        <label>{itm}</label>
                      </div>
                    </Command.Item>
                  ))}
                </>
              )}
            </Command.List>
          </Command>

          <div className="flex text-xs font-medium text-[#5d74a0]">
            <button
              onClick={() => onClear()}
              className="w-1/2 border h-full py-3"
            >
              Clear
            </button>
            <button
              onClick={() => onFilterSave(filter, selected)}
              className="w-1/2 border h-full py-3"
            >
              Save
            </button>
          </div>
        </Popover.Content>
      </Popover.Root>
    </>
  );
};

const FilterItem = ({ name, remove }: { name: string; remove: () => void }) => {
  return (
    <div className="flex gap-x-2 px-4 py-3 border border-gray-300">
      <Popover.Trigger asChild>
        <button className="flex gap-x-2">
          <span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="#344054"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <span className="font-semibold text-gray-700">{name}</span>
        </button>
      </Popover.Trigger>
      <button onClick={remove}>
        <span>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_582_3550)">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="#98A2B3"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_582_3550">
                <rect width="20" height="20" rx="8" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </span>
      </button>
    </div>
  );
};
